<script setup lang="ts">
import { QDrawer, useQuasar } from 'quasar';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import type { Partner } from '@/clients/v2';

import { errorMessage } from '@/composables/Notify.ts';
import { captureError } from '@/composables/Sentry.ts';
import { defaultDrawerIds } from '@/utils/DataTestIds.ts';
import { userInitials } from '@/utils/formatting.ts';
import { useSidebar } from './useSidebar.ts';

import { useAuthStore } from '@/stores/AuthStore.ts';
import {
  SIDEBAR_MINI_WIDTH,
  SIDEBAR_OVERLAY_WIDTH,
  SIDEBAR_WIDTH,
  useStylesStore,
} from '@/stores/ui/StylesStore.ts';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';
import { usePartnersStore } from '@/stores/v2/PartnerStore.ts';

import Icon from '@/components/atoms/Icon.vue';
import DProfile from '@/components/dialogs/DProfile.vue';

import AScrollArea from '@/components/atoms/AScrollArea.vue';

const $q = useQuasar();
const { t } = useI18n();
const route = useRoute();
const authStore = useAuthStore();
const stylesStore = useStylesStore();
const partnersStore = usePartnersStore();
const organizationsStore = useOrganizationsStore();
const {
  sidebarTopItems,
  sidebarMiddleItems,
  activeTitle,
  isProjectRoute,
  isProjectLoading,
  isItemActive,
} = useSidebar();

const organizationPartner = ref<Partner | null>(null);
const drawer = ref<QDrawer | null>(null);

const showToggle = ref(false);
const showDialog = ref(false);

// Quasar ignores `mini` on a phone.
const miniState = computed(() => !$q.screen.lt.sm && !stylesStore.drawerOpened);

function openDrawer() {
  stylesStore.toggleDrawer(true);
}

function closeDrawer() {
  showToggle.value = false;
  stylesStore.toggleDrawer(false);
  // On a phone the overlay follows its own flag, not the drawer's width.
  if ($q.screen.lt.sm) stylesStore.showDrawer(false);
}

function handleMouseOver() {
  showToggle.value = true;
}

function handleMouseOut() {
  showToggle.value = false;
}

// On a tablet the drawer overlays the page: fold it back after a pick, the current page included.
function foldOverlay() {
  if ($q.screen.sm) stylesStore.toggleDrawer(false);
}
watch(() => route.path, foldOverlay);

watch(
  () => organizationsStore.configData,
  async (newValue) => {
    if (!newValue) return;

    if (!newValue.partner_id) {
      organizationPartner.value = null;
      return;
    }

    try {
      const partner = await partnersStore.getPartner({ partnerId: newValue.partner_id });

      organizationPartner.value = partner || null;
    } catch (error) {
      errorMessage(error);

      captureError(error, {
        message: 'Component DefaultSidebar, Watch organizationsStore.configData',
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <q-drawer
    ref="drawer"
    :model-value="stylesStore.drawerShowing"
    :mini="miniState"
    mini-to-overlay
    :mini-width="SIDEBAR_MINI_WIDTH"
    show-if-above
    :width="$q.screen.lt.md ? SIDEBAR_OVERLAY_WIDTH : SIDEBAR_WIDTH"
    :breakpoint="$q.screen.sizes.sm - 1"
    side="left"
    @update:model-value="stylesStore.showDrawer"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseOut"
  >
    <nav class="column fit" :aria-label="t('Main menu')">
      <q-list padding>
        <!-- The close button is the link's sibling: a control nested in a link is skipped by screen readers. -->
        <div id="logo" class="row no-wrap items-center q-mb-md">
          <q-item
            v-ripple
            to="/dashboard"
            clickable
            :aria-label="t('Modulos home')"
            class="col modulos-logo text-body2 text-inverse"
            @click="foldOverlay"
          >
            <div class="sections__container row full-width justify-center">
              <q-item-section avatar>
                <Icon icon-name="modulos-logo" icon-folder="drawer" icon-size="26px" />
              </q-item-section>

              <q-item-section :style="{ marginLeft: '14px' }">
                <Icon icon-name="modulos-logo-text" icon-folder="drawer" />
              </q-item-section>
            </div>
          </q-item>
          <!-- Phone drawer: its own close and greeting; no navigation, phones are not finished. -->
          <!-- eslint-disable vue/no-restricted-class -->
          <q-btn
            flat
            round
            dense
            icon="o_close"
            class="lt-sm q-mr-md text-inverse"
            :aria-label="t('Close menu')"
            @click="stylesStore.showDrawer(false)"
          />
        </div>
        <div v-if="authStore.user && !miniState" class="user__greetings lt-sm justify-center">
          <q-item :clickable="false" class="text-body2 text-inverse">
            <div class="sections__container row full-width justify-center">
              <q-item-section>
                <span class="text-body1 text-bold text-inverse">
                  {{
                    t('Hello, ') + authStore.user.firstname + ' ' + authStore.user.lastname
                  }}</span
                >
                <span
                  v-if="'organization' in authStore.user"
                  class="user__organization text-caption q-mt-xs"
                  >{{ authStore.user.organization.name }}</span
                >
              </q-item-section>
            </div>
          </q-item>
        </div>
        <div class="top-icons__container gt-xs">
          <div v-for="item in sidebarTopItems" :key="item.name">
            <q-item
              v-if="item.showCondition"
              v-ripple
              :to="item.to ? item.to : null"
              :href="item.href"
              :target="item.href ? '_blank' : undefined"
              clickable
              class="text-body2"
              :class="{
                'nav-item--current text-bold': isItemActive(item),
              }"
              :aria-label="item.label"
              :aria-current="isItemActive(item) ? 'location' : undefined"
              :data-test-id="item.dataTestId"
              @click="foldOverlay"
            >
              <div class="sections__container row full-width justify-center">
                <q-item-section avatar>
                  <q-icon :name="item.icon" size="20px" />
                </q-item-section>
                <q-item-section v-if="!miniState" :style="{ marginLeft: '14px' }">
                  <div class="row items-center">
                    <span>{{ item.label }}</span>
                  </div>
                </q-item-section>
                <q-tooltip anchor="center right" self="center left" :offset="[5, 5]">
                  {{ item.label }}
                </q-tooltip>
              </div>
            </q-item>
          </div>
        </div>
      </q-list>

      <div
        v-if="sidebarMiddleItems && sidebarMiddleItems.length > 0"
        class="middle-icons__container gt-xs col column"
        :class="{ 'middle-icons__container--plate': !miniState }"
      >
        <!-- eslint-enable vue/no-restricted-class -->
        <!-- Always visible: the only sign the menu continues below. -->
        <AScrollArea height-size="0" no-padding visible class="col">
          <div :class="{ plate: !miniState }" :aria-busy="isProjectLoading">
            <template v-if="(activeTitle || isProjectLoading) && !miniState">
              <router-link
                v-if="isProjectRoute"
                :to="{ name: 'projects' }"
                class="plate__back block"
              >
                <span
                  class="plate__eyebrow row items-center no-wrap q-mb-xs text-caption text-weight-semibold"
                >
                  <q-icon name="o_arrow_back" size="14px" />{{ t('AI Registry') }}
                </span>
                <q-skeleton
                  v-if="isProjectLoading"
                  type="text"
                  animation="wave"
                  width="70%"
                  class="plate__skeleton text-body2"
                />
                <h6 v-else class="plate__name text-body2 text-weight-semibold text-ellipsis">
                  {{ activeTitle }}
                </h6>
                <q-tooltip
                  v-if="!isProjectLoading"
                  anchor="center right"
                  self="center left"
                  :offset="[5, 0]"
                >
                  <div class="text-weight-semibold">{{ activeTitle }}</div>
                  <div>{{ t('Back to AI Systems') }}</div>
                </q-tooltip>
              </router-link>
              <h6 v-else class="plate__eyebrow plate__heading text-ellipsis">
                {{ activeTitle }}
              </h6>
            </template>

            <template v-if="isProjectLoading">
              <q-item v-for="item in sidebarMiddleItems" :key="item.name" aria-hidden="true">
                <div class="sections__container row full-width justify-center">
                  <q-item-section avatar>
                    <q-skeleton type="rect" animation="wave" size="20px" class="plate__skeleton" />
                  </q-item-section>
                  <q-item-section v-if="!miniState" :style="{ marginLeft: '10px' }">
                    <q-skeleton type="text" animation="wave" class="plate__skeleton text-body2" />
                  </q-item-section>
                </div>
              </q-item>
            </template>
            <div v-for="item in sidebarMiddleItems" v-else :key="item.name">
              <q-item
                v-ripple
                :to="item.to ? item.to : null"
                :active="isItemActive(item)"
                :aria-current="isItemActive(item) ? 'page' : undefined"
                :aria-label="item.label"
                clickable
                class="text-body2"
                :data-test-id="item.dataTestId"
                @click="foldOverlay"
              >
                <div class="sections__container row full-width justify-center">
                  <q-item-section v-if="item.to" avatar>
                    <q-icon :name="item.icon" size="20px" />
                  </q-item-section>
                  <!-- Resting labels wrap 4px early, so the wider bold label never needs an extra row. -->
                  <q-item-section
                    v-if="!miniState"
                    :class="isItemActive(item) ? 'text-bold' : 'q-pr-xs'"
                    :style="{ marginLeft: '10px' }"
                  >
                    <div class="row items-center">
                      <span>{{ item.label }}</span>
                    </div>
                  </q-item-section>
                  <q-tooltip anchor="center right" self="center left" :offset="[5, 5]">
                    {{ item.label }}
                  </q-tooltip>
                </div>
              </q-item>
            </div>
          </div>
        </AScrollArea>
      </div>
      <q-space v-else />
      <q-item
        v-if="authStore.user"
        v-ripple
        class="drawer-profile text-body2 text-inverse"
        :data-test-id="defaultDrawerIds.drawerUserProfile"
        clickable
        role="button"
        aria-haspopup="dialog"
        :aria-label="t('User Profile')"
        @click="showDialog = true"
      >
        <div class="sections__container row full-width justify-center">
          <q-item-section avatar>
            <span class="text-caption text-inverse" aria-hidden="true">
              {{ userInitials(authStore.user) }}
            </span>
          </q-item-section>

          <q-item-section v-if="!miniState" :style="{ marginLeft: '14px' }">
            {{ t('User Profile') }}
          </q-item-section>
        </div>
      </q-item>
      <q-item
        v-if="organizationsStore.configData && organizationPartner"
        v-ripple
        class="drawer-partner text-body2 text-inverse"
      >
        <div
          class="row full-width"
          :class="miniState ? 'justify-center' : 'sections__container justify-start'"
        >
          <q-item-section v-if="miniState" avatar class="cursor-pointer" @click="openDrawer">
            <Icon icon-folder="partner" :icon-name="`${organizationPartner.code}-small`" />
          </q-item-section>

          <q-item-section v-else class="cursor-pointer" @click="closeDrawer">
            <div class="row items-center no-wrap">
              <span class="text-caption text-muted q-mr-sm">Partner</span>
              <Icon icon-folder="partner" :icon-name="organizationPartner.code" />
            </div>
          </q-item-section>
        </div>
      </q-item>
    </nav>
    <!-- On a phone the header's menu button opens and closes the drawer. -->
    <button
      v-if="!$q.screen.lt.sm"
      type="button"
      class="sidebar-toggle row items-center justify-center"
      :class="[showToggle ? 'show-toggle' : '']"
      :aria-label="miniState ? t('Expand menu') : t('Collapse menu')"
      :aria-expanded="!miniState"
      :data-test-id="miniState ? defaultDrawerIds.drawerOpenIcon : defaultDrawerIds.drawerCloseIcon"
      @click="miniState ? openDrawer() : closeDrawer()"
      @mouseover="handleMouseOver"
      @mouseleave="handleMouseOut"
    >
      <q-icon
        :name="miniState ? 'o_keyboard_double_arrow_right' : 'o_keyboard_double_arrow_left'"
        color="teal-3"
        size="18px"
      />
    </button>
    <DProfile
      v-if="showDialog && authStore.user"
      :show-dialog="showDialog"
      @hide="showDialog = false"
    />
  </q-drawer>
</template>

<style lang="scss" scoped>
:deep(aside) {
  z-index: 5001;
  .q-drawer__content {
    background: $gradient-brand;

    .q-list {
      padding: 0 !important;
    }

    .q-item {
      line-height: 20px;
      padding: 4px 16px !important;
      min-height: 40px;
      .sections__container {
        padding: 0 8px;
        border-radius: 5px;
        flex-wrap: nowrap;
        position: relative;

        .q-item__section div svg:hover {
          cursor: pointer;
        }
        .q-item__section div a:hover {
          cursor: pointer;
          background: rgba(255, 255, 255, 0.3);
        }
      }
      .q-item__section {
        min-width: unset !important;
      }
      .q-item__section--side {
        padding-right: 0 !important;
      }
      .q-focus-helper {
        background: transparent !important;
        opacity: 0 !important;
      }
      .q-ripple {
        display: none;
      }
    }

    .modulos-logo {
      padding: 21.5px 16px !important;
    }

    .top-icons__container,
    .middle-icons__container {
      .sections__container {
        color: var(--color-text-inverse-muted);
      }

      .q-item--clickable:hover .sections__container,
      .nav-item--current .sections__container {
        color: var(--color-text-inverse);
      }
    }

    .middle-icons__container .q-item--active .sections__container {
      background: rgba($primary-300, 0.24);
      color: var(--color-text-inverse);
    }

    .q-item--clickable:hover .sections__container {
      background: var(--color-surface-inverse-hover);
    }

    .q-item:focus-visible {
      outline: none;

      .sections__container {
        outline: 2px solid var(--color-focus-ring-inverse);
        outline-offset: -2px;
      }
    }

    #logo {
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      .sections__container {
        background: transparent !important;
      }
    }

    .bottom-icons__container {
      width: 100%;
    }

    .drawer-profile,
    .drawer-partner,
    .middle-icons__container {
      border-top: 1px solid rgba(255, 255, 255, 0.2);
    }

    .middle-icons__container {
      min-height: 0;
      margin-top: 16px;
      padding-top: 24px;

      &--plate {
        margin-top: 14px;
        padding-top: 0;
        border-top: none;
      }

      .q-scrollarea__content {
        width: 100%;
      }
    }

    .plate {
      margin: 0 16px;
      padding: 10px 0 12px;
      background: var(--color-surface-inverse-raised);
      border: 1px solid var(--color-border-inverse);
      border-radius: 12px;

      .q-item {
        padding: 4px !important;
      }

      &__heading,
      &__back {
        padding: 2px 12px 8px;
      }

      &__eyebrow {
        gap: 3px;
        color: var(--color-text-inverse-muted);
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      &__back {
        text-decoration: none;

        &:hover .plate__eyebrow {
          color: var(--color-text-inverse);
        }

        &:focus-visible {
          outline: 2px solid var(--color-focus-ring-inverse);
          outline-offset: -2px;
          border-radius: 5px;
        }
      }

      &__name {
        color: var(--color-text-inverse);
      }

      &__skeleton {
        background: var(--color-surface-inverse-hover);
      }
    }

    .user__organization {
      color: var(--color-text-inverse-muted);
    }

    .drawer-profile {
      min-height: 45px;
      .sections__container {
        span {
          height: 25px;
          width: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: $secondary-600;
          border-radius: 50%;
        }
      }
    }

    .drawer-partner {
      background-color: $white;

      .sections__container {
        padding: 0;
      }
    }

    .sidebar-toggle {
      width: 20.6px;
      height: 28.6px;
      cursor: pointer;
      position: absolute;
      right: -20px;
      top: 0;
      background: $secondary-700;
      border-top-right-radius: 3px;
      border-bottom-right-radius: 3px;
      z-index: $z-index-very-high;
      transition: all 0.2s ease;
      opacity: 0.5;
      border: 0;
      padding: 0;
      &:hover,
      &:focus-visible {
        opacity: 1;
      }
      &:focus-visible {
        outline: 2px solid var(--color-focus-ring-inverse);
        outline-offset: -2px;
      }
      // No hover on touch to reveal it; 24px for a finger.
      @media (pointer: coarse) {
        width: 24px;
        height: 32px;
        right: -24px;
        opacity: 1;
      }
    }

    .show-toggle {
      opacity: 1;
    }
  }
}

.q-drawer--mini-animate :deep(.middle-icons__container) {
  .q-item__section--main,
  .plate__heading,
  .plate__back {
    visibility: hidden;
  }
}

.q-drawer--mini {
  .q-item {
    text-align: unset !important;
    justify-content: unset !important;
    min-width: unset !important;
  }
}
</style>
